import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { statsAPI } from '../services/api';
import { format } from 'date-fns';
import {
    FiAlertTriangle, FiActivity, FiShield,
    FiSearch, FiRefreshCw, FiSkipForward, FiSkipBack,
    FiZap, FiUser
} from 'react-icons/fi';
import '../styles/Statistics.css';

const TYPE_TABS = [
    { key: 'all',       label: 'Tất Cả',    icon: <FiActivity /> },
    { key: 'damage',    label: 'Sát Thương', icon: <FiZap /> },
    { key: 'death',     label: 'Tử Vong',    icon: <FiUser /> },
    { key: 'anticheat', label: 'Chống Hack', icon: <FiShield /> },
];

const TYPE_BADGE = {
    damage:    { label: 'Sát Thương', cls: 'badge-damage' },
    death:     { label: 'Tử Vong',    cls: 'badge-death' },
    anticheat: { label: 'Hack',       cls: 'badge-anticheat' },
};

const PAGE_SIZE = 100;

const fmtTime = (ts) => {
    if (!ts) return '—';
    try { return format(new Date(ts), 'dd/MM HH:mm:ss'); } catch { return String(ts); }
};

// ─── Column definitions per tab ───────────────────────────────────────────────
const HEADERS = {
    all:       ['Loại', 'Thời gian', 'Chi tiết', ''],
    damage:    ['Loại', 'Thời gian', 'Người tấn công', 'Nạn nhân (ID/PID)', 'Vũ khí', 'Sát thương', ''],
    death:     ['Loại', 'Thời gian', 'Người chơi', 'Nguyên nhân', 'Kẻ giết', ''],
    anticheat: ['Loại', 'Thời gian', 'Người chơi', 'Steam ID', 'Phát hiện', 'Chi tiết', ''],
};

// Player tag với ID/PID badges
const PlayerTag = ({ name, id, pid, steam, color }) => (
    <span className="plc-player-tag" style={color ? { color } : null}>
        <b>{name || '?'}</b>
        {id  != null && <span className="plc-id-badge">ID:{id}</span>}
        {pid != null && <span className="plc-pid-badge">PID:{pid}</span>}
        {steam && !id  && <span className="plc-mono plc-text-muted">[{steam.slice(-10)}]</span>}
    </span>
);

const Row = ({ log, tab }) => {
    const [exp, setExp] = useState(false);
    const badge = TYPE_BADGE[log._type] || { label: log._type, cls: '' };
    const isHack = log._type === 'anticheat';

    const typeCell = <td><span className={`plc-badge ${badge.cls}`}>{badge.label}</span></td>;
    const timeCell = <td className="plc-time">{fmtTime(log.timestamp)}</td>;

    // Snapshot panel: hiện toàn bộ field structured khi expand (giống Discord embed)
    const snapshot = log._type === 'damage' ? (
        <div className="plc-snapshot">
            <div className="plc-snap-row"><span className="plc-k">⚔️ Người tấn công</span><span className="plc-v"><b>{log.attacker || '?'}</b></span></div>
            <div className="plc-snap-row"><span className="plc-k">🎯 Nạn nhân</span>
                <span className="plc-v"><b>{log.victim}</b>
                    {log.victimId  != null && <span className="plc-id-badge">ID:{log.victimId}</span>}
                    {log.victimPid != null && <span className="plc-pid-badge">PID:{log.victimPid}</span>}
                </span>
            </div>
            {log.weapon &&    <div className="plc-snap-row"><span className="plc-k">🔫 Vũ khí</span><span className="plc-v">{log.weapon}</span></div>}
            {log.damage  != null && <div className="plc-snap-row"><span className="plc-k">🩸 Sát thương</span><span className="plc-v plc-damage">-{log.damage} HP</span></div>}
            {log.currentHp != null && <div className="plc-snap-row"><span className="plc-k">❤️ Máu sau khi bị</span><span className="plc-v">{log.currentHp}/{log.maxHp || 100}</span></div>}
            {log.victimSteam && <div className="plc-snap-row"><span className="plc-k">🎮 Steam</span><span className="plc-v plc-mono">{log.victimSteam}</span></div>}
        </div>
    ) : log._type === 'death' ? (
        <div className="plc-snapshot">
            <div className="plc-snap-row"><span className="plc-k">💀 Nạn nhân</span><span className="plc-v"><b>{log.player}</b>{log.playerId && <span className="plc-id-badge">{log.playerId}</span>}</span></div>
            {log.killer && <div className="plc-snap-row"><span className="plc-k">⚔️ Kẻ giết</span><span className="plc-v"><b className="plc-killer">{log.killer}</b></span></div>}
            {log.cause && <div className="plc-snap-row"><span className="plc-k">📋 Nguyên nhân</span><span className="plc-v">{log.cause}</span></div>}
        </div>
    ) : null;

    let cells = null;

    if (tab === 'all') {
        let detail = null;
        if (log._type === 'damage') {
            detail = (
                <>
                    <b>{log.attacker}</b>{' → '}
                    <PlayerTag name={log.victim} id={log.victimId} pid={log.victimPid} />
                    {log.weapon && <span className="plc-text-muted"> [{log.weapon}]</span>}
                    {log.damage != null && <span className="plc-damage-inline"> -{log.damage} HP</span>}
                </>
            );
        } else if (log._type === 'death') {
            detail = (
                <>
                    <b>{log.player}</b>{' chết'}
                    {log.killer ? <> bởi <b className="plc-killer">{log.killer}</b></> : null}
                    {log.cause ? <span className="plc-text-muted"> ({log.cause})</span> : null}
                </>
            );
        } else if (log._type === 'anticheat') {
            detail = (
                <>
                    <b>{log.player}</b>{' — '}
                    <span className="plc-detection">{log.detection}</span>
                    {log.steamId && <span className="plc-mono plc-text-muted"> [{log.steamId.slice(-10)}]</span>}
                </>
            );
        }
        cells = <td className="plc-detail-cell">{detail}</td>;
    } else if (tab === 'damage' && log._type === 'damage') {
        cells = (
            <>
                <td><b>{log.attacker || '?'}</b></td>
                <td><PlayerTag name={log.victim} id={log.victimId} pid={log.victimPid} /></td>
                <td className="plc-weapon">{log.weapon || '—'}</td>
                <td>{log.damage != null ? <span className="plc-damage">-{log.damage} HP</span> : '—'}</td>
            </>
        );
    } else if (tab === 'death' && log._type === 'death') {
        cells = (
            <>
                <td><span className="plc-name">{log.player}</span></td>
                <td className="plc-cause">{log.cause || '—'}</td>
                <td>{log.killer
                    ? <span className="plc-killer">{log.killer}</span>
                    : <span className="plc-text-muted">—</span>}
                </td>
            </>
        );
    } else if (tab === 'anticheat' && log._type === 'anticheat') {
        cells = (
            <>
                <td><span className="plc-name">{log.player}</span></td>
                <td className="plc-mono plc-steamid">{log.steamId || '—'}</td>
                <td><span className="plc-detection">{log.detection}</span></td>
                <td className="plc-detail-text" title={log.details}>
                    {log.details ? log.details.substring(0, 80) + (log.details.length > 80 ? '…' : '') : '—'}
                </td>
            </>
        );
    } else {
        return null;
    }

    const expandable = !!snapshot;
    const colSpan = (HEADERS[tab] || HEADERS.all).length;

    return (
        <>
            <tr className={isHack ? 'row-anticheat' : ''} onClick={() => expandable && setExp(v => !v)} style={expandable ? { cursor: 'pointer' } : null}>
                {typeCell}
                {timeCell}
                {cells}
                <td className="plc-exp-cell">{expandable ? (exp ? '▾' : '▸') : ''}</td>
            </tr>
            {exp && snapshot && (
                <tr className="plc-snapshot-row">
                    <td colSpan={colSpan}>{snapshot}</td>
                </tr>
            )}
        </>
    );
};

const Statistics = () => {
    const [logs, setLogs]       = useState([]);
    const [counts, setCounts]   = useState({ damage: 0, death: 0, anticheat: 0 });
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('all');
    const [search, setSearch]   = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [days, setDays]       = useState(30);
    const [page, setPage]       = useState(1);

    const fetchLogs = useCallback(async () => {
        try {
            setLoading(true);
            const res = await statsAPI.getPlayerCombat({
                type: activeTab,
                limit: 1000,
                search,
                days,
            });
            setLogs(res.data.logs || []);
            setCounts(res.data.counts || { damage: 0, death: 0, anticheat: 0 });
        } catch (err) {
            console.error(err);
            toast.error('Không thể tải log người chơi');
        } finally {
            setLoading(false);
        }
    }, [activeTab, search, days]);

    useEffect(() => {
        setPage(1);
        fetchLogs();
    }, [fetchLogs]);

    const handleSearch = (e) => {
        e.preventDefault();
        setSearch(searchInput);
    };

    const handleTabChange = (key) => {
        setActiveTab(key);
        setPage(1);
    };

    const totalPages = Math.ceil(logs.length / PAGE_SIZE);
    const pageLogs   = logs.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    const totalAll   = counts.damage + counts.death + counts.anticheat;
    const headers    = HEADERS[activeTab] || HEADERS.all;

    return (
        <div className="plc-page">
            {/* Header */}
            <div className="plc-header">
                <div>
                    <h1 className="plc-title">Log Người Chơi</h1>
                    <p className="plc-subtitle">Sát thương · Tử vong · Phát hiện hack</p>
                </div>
                <div className="plc-header-actions">
                    <select
                        className="plc-select"
                        value={days}
                        onChange={e => { setDays(parseInt(e.target.value)); setPage(1); }}
                    >
                        <option value={7}>7 ngày</option>
                        <option value={14}>14 ngày</option>
                        <option value={30}>30 ngày</option>
                    </select>
                    <button className="plc-refresh-btn" onClick={fetchLogs} disabled={loading}>
                        <FiRefreshCw className={loading ? 'spinning' : ''} />
                        <span>Làm mới</span>
                    </button>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="plc-summary">
                <div className="plc-card">
                    <span className="plc-card-value">{totalAll.toLocaleString()}</span>
                    <span className="plc-card-label">Tổng log</span>
                </div>
                <div className="plc-card damage">
                    <FiZap className="plc-card-icon" />
                    <span className="plc-card-value">{counts.damage.toLocaleString()}</span>
                    <span className="plc-card-label">Sát thương</span>
                </div>
                <div className="plc-card death">
                    <FiUser className="plc-card-icon" />
                    <span className="plc-card-value">{counts.death.toLocaleString()}</span>
                    <span className="plc-card-label">Tử vong</span>
                </div>
                <div className="plc-card anticheat">
                    <FiAlertTriangle className="plc-card-icon" />
                    <span className="plc-card-value">{counts.anticheat.toLocaleString()}</span>
                    <span className="plc-card-label">Hack / Gian lận</span>
                </div>
            </div>

            {/* Controls */}
            <div className="plc-controls">
                <div className="plc-tabs">
                    {TYPE_TABS.map(t => (
                        <button
                            key={t.key}
                            className={`plc-tab ${activeTab === t.key ? 'active' : ''} tab-${t.key}`}
                            onClick={() => handleTabChange(t.key)}
                        >
                            {t.icon}
                            <span>{t.label}</span>
                            {t.key !== 'all' && (
                                <span className="plc-tab-count">{counts[t.key] || 0}</span>
                            )}
                        </button>
                    ))}
                </div>

                <form className="plc-search-form" onSubmit={handleSearch}>
                    <div className="plc-search-wrap">
                        <FiSearch className="plc-search-icon" />
                        <input
                            className="plc-search-input"
                            placeholder="Tìm tên người chơi, vũ khí..."
                            value={searchInput}
                            onChange={e => setSearchInput(e.target.value)}
                        />
                    </div>
                    <button type="submit" className="plc-search-btn">Tìm</button>
                    {search && (
                        <button type="button" className="plc-clear-btn"
                            onClick={() => { setSearchInput(''); setSearch(''); }}>
                            Xóa
                        </button>
                    )}
                </form>
            </div>

            {/* Table */}
            <div className="plc-table-wrap">
                {loading ? (
                    <div className="plc-loading">
                        <div className="loading-spinner"></div>
                        <p>Đang tải...</p>
                    </div>
                ) : pageLogs.length === 0 ? (
                    <div className="plc-empty">
                        <FiActivity className="plc-empty-icon" />
                        <p>Không có log nào</p>
                        {search && <p className="plc-empty-sub">Thử xóa bộ lọc tìm kiếm</p>}
                    </div>
                ) : (
                    <table className="plc-table">
                        <thead>
                            <tr>
                                {headers.map(h => <th key={h}>{h}</th>)}
                            </tr>
                        </thead>
                        <tbody>
                            {pageLogs.map((log, i) => (
                                <Row key={i} log={log} tab={activeTab} />
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Pagination */}
            {!loading && totalPages > 1 && (
                <div className="plc-pagination">
                    <button className="plc-page-btn" onClick={() => setPage(1)} disabled={page === 1}>
                        <FiSkipBack />
                    </button>
                    <button className="plc-page-btn" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                        Trước
                    </button>
                    <span className="plc-page-info">
                        Trang {page} / {totalPages}
                        <span className="plc-page-total"> ({logs.length.toLocaleString()} kết quả)</span>
                    </span>
                    <button className="plc-page-btn" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                        Sau
                    </button>
                    <button className="plc-page-btn" onClick={() => setPage(totalPages)} disabled={page === totalPages}>
                        <FiSkipForward />
                    </button>
                </div>
            )}
        </div>
    );
};

export default Statistics;
